/**
 * URL Redirects Configuration - FRONTEND ONLY
 * ============================================
 * IMPORTANT: This file handles FRONTEND-ONLY client-side redirects.
 * 
 * Backend middleware (server.py canonical_redirect) handles:
 * - Trailing slash removal
 * - www → non-www
 * - HTTP → HTTPS  
 * - /service/* → /services/* redirects
 * 
 * DO NOT duplicate redirects that are already in backend middleware
 * to avoid redirect chains (multiple hops).
 */

export const urlRedirects = {
  // ============== OLD WORDPRESS/CMS URLs (Frontend handles these) ==============
  // These are NOT in backend middleware, so frontend is the single source
  
  // Old WordPress blog URL from GSC
  '/2025-guide-how-brand-owners-can-unlock-high-reach-visibility-on-premium-marketplaces': '/blog/2025-guide-brand-visibility-premium-marketplaces',
  
  // Phase X.6 (Feb 24, 2026) — Zaroori Jini OMS canonical migration.
  // Every /oms/* URL 301-redirects to /zaroori-jini-oms/* so Google
  // consolidates 90+ days of authority into the new SoftwareApplication entity URL.
  '/oms': '/zaroori-jini-oms',
  '/oms/': '/zaroori-jini-oms',
  '/oms/pricing': '/zaroori-jini-oms/pricing',
  '/oms/features': '/zaroori-jini-oms/features',
  '/oms/roi-calculator': '/zaroori-jini-oms/roi-calculator',

  // Old WordPress service pages (marketing URLs)
  '/myntra-onboarding': '/services/myntra',
  // Sprint 4 (Feb 2026): /myntra-seller-registration is now a live Sprint 4 page

  // Phase X.5 (Feb 24, 2026): consolidate Myntra keyword cannibalisation.
  // /services/myntra is the canonical ranking URL for the query "Myntra agency".
  // These four sibling URLs each attracted 1-3 impressions/6mo — safe to consolidate.
  '/myntra/best-myntra-onboarding-service-india': '/myntra-seller-onboarding',

  '/nykaa-onboarding': '/services/nykaa',
  // Sprint 4 (Feb 2026): /nykaa-seller-registration is now a live Sprint 4 page
  '/ajio-onboarding': '/services/ajio',
  // Sprint 4 (Feb 2026): /ajio-seller-registration is now a live Sprint 4 page
  '/tata-cliq-onboarding': '/services/tata-cliq',
  '/blinkit-onboarding': '/services/blinkit',
  '/zepto-onboarding': '/services/zepto',
  
  // Old WordPress category/archive pages
  '/category/marketplace': '/services',
  '/category/ecommerce': '/blog',
  '/category/fashion': '/services',
  '/category/beauty': '/services',
  '/category/quick-commerce': '/services',
  
  // Old blog URLs
  '/blog/category/marketplace': '/blog',
  '/blog/category': '/blog',
  '/articles': '/blog',
  '/news': '/blog',
  
  // Old page URL aliases
  '/about-us': '/about',
  '/aboutus': '/about',
  '/contact-us': '/contact',
  '/contactus': '/contact',
  '/get-in-touch': '/contact',
  '/plans': '/products',
  '/packages': '/products',
  '/our-services': '/services',
  '/our-team': '/team',
  '/founder': '/team',
  '/ceo': '/team',
  '/testimonials': '/success-stories',
  '/case-studies': '/success-stories',
  '/clients': '/success-stories',
  '/portfolio': '/success-stories',
  
  // Old policy page aliases
  '/privacy': '/privacy-policy',
  '/terms': '/terms-of-service',
  '/terms-and-conditions': '/terms-of-service',
  '/refund': '/refund-policy',
  '/cancellation-policy': '/refund-policy',
  
  // Common typos
  '/home': '/',
  '/index': '/',
  '/blogs': '/blog',
  
  // Old marketplace marketing URLs
  '/sell-on-myntra': '/services/myntra',
  '/sell-on-nykaa': '/services/nykaa',
  '/sell-on-ajio': '/services/ajio',
  '/sell-on-amazon': '/services',
  '/sell-on-flipkart': '/services',
  '/how-to-sell-on-myntra': '/services/myntra',
  '/how-to-sell-on-nykaa': '/services/nykaa',
  '/how-to-sell-on-ajio': '/services/ajio',
  
  // Career/Jobs pages
  '/careers': '/life-at-zaroori',
  '/jobs': '/life-at-zaroori',
  '/work-with-us': '/life-at-zaroori',
  '/join-us': '/life-at-zaroori',
  
  // Pillar page marketing URLs
  '/tira-onboarding': '/tira-seller-onboarding-guide',
  '/tira-seller-guide': '/tira-seller-onboarding-guide',
  '/sell-on-tira': '/tira-seller-onboarding-guide',
  '/how-to-sell-on-tira': '/tira-seller-onboarding-guide',
  '/pernias-onboarding': '/pernias-pop-up-shop-seller-guide',
  '/pernias-seller-guide': '/pernias-pop-up-shop-seller-guide',
  '/sell-on-pernias': '/pernias-pop-up-shop-seller-guide',
  '/pernias-popup-shop': '/pernias-pop-up-shop-seller-guide',
  '/pernia-pop-up-shop': '/pernias-pop-up-shop-seller-guide',
  
  // Legacy blog posts
  '/why-most-brands-fail-on-myntra-nykaa': '/blog/step-by-step-listing-services-myntra-nykaa-ajio',
  '/why-brands-fail-myntra': '/blog/how-to-sell-on-myntra-seller-registration-guide',
  '/common-mistakes-marketplace': '/blog',
};

/**
 * Check if a path should be redirected
 * @param {string} path - The current URL path
 * @returns {string|null} - The redirect target or null if no redirect
 * 
 * NOTE: Trailing slash handling is done by backend middleware.
 * This function only handles semantic URL redirects.
 */
export const getRedirectPath = (path) => {
  // Normalize path (lowercase)
  // Remove trailing slash for consistent matching (backend handles slash normalization)
  const normalizedPath = path.toLowerCase().replace(/\/$/, '') || '/';
  
  // Direct match only - no dual slash logic to avoid chains
  if (urlRedirects[normalizedPath]) {
    return urlRedirects[normalizedPath];
  }
  
  return null;
};

export default urlRedirects;
